import {
  slugify,
  type CreateVisaPricingTierRequest,
  type CreateVisaRequest,
  type VisaDetail,
  type VisaPricingTierOut,
} from '@utazzo/shared';
import {
  UzAlert,
  UzBadge,
  UzButton,
  UzCard,
  UzDateTimePicker,
  UzForm,
  UzFormField,
  UzInput,
  UzNumberInput,
  UzSelect,
  UzSwitch,
  UzTabs,
  UzTextarea,
  UzTooltip,
} from '@utazzo/ui';
import { ArrowLeft, Plus, Sparkles, Trash2 } from 'lucide-react';
import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useForm, type UseFormReturn } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import {
  useApproveVisa,
  useCreateVisa,
  useCreateVisaPricingTier,
  useDeleteVisaPricingTier,
  useUpdateVisa,
  useUpdateVisaPricingTier,
  useVisaDetail,
  useVisaPricingTiers,
} from '../use-visas';

import { PhotoGalleryField, type GalleryPhoto } from '@/components/photo-gallery';
import { CountryPicker, CurrencyPicker, SupplierPicker } from '@/components/pickers';
import { CatalogEditorSkeleton } from '@/components/skeletons';
import { useCurrentUser } from '@/features/auth/use-auth';
import { useCurrenciesList } from '@/features/currencies/use-currencies';
import { RichTextEditor } from '@/features/website-cms/components/RichTextEditor';
import { useListReturn } from '@/hooks/use-list-return';

/**
 * Full-page Visa edit, in the shared catalog-editor mold (matches Hotel /
 * Activity / Transfer): slim top bar + read-only legacy metadata strip + tabs
 * (Details / Pricing tiers / Content & media), each a main column of Cards plus
 * a 320px sidebar.
 *
 *   /visas/new          mode=create
 *   /visas/:id/edit     mode=edit
 *
 * Pricing tiers are managed as a sub-resource (one row per urgency: Standard,
 * Express, Same-day) and only show for existing visas.
 */

const STATUS_OPTIONS = [
  { value: 'DRAFT', label: 'Draft' },
  { value: 'PENDING_APPROVAL', label: 'Pending approval' },
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const VISA_TYPE_OPTIONS = [
  { value: 'TOURIST', label: 'Tourist' },
  { value: 'BUSINESS', label: 'Business' },
  { value: 'TRANSIT', label: 'Transit' },
  { value: 'WORK', label: 'Work' },
  { value: 'STUDENT', label: 'Student' },
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'DIPLOMATIC', label: 'Diplomatic' },
];

const ENTRY_TYPE_OPTIONS = [
  { value: 'SINGLE', label: 'Single entry' },
  { value: 'DOUBLE', label: 'Double entry' },
  { value: 'MULTIPLE', label: 'Multiple entry' },
];

// ADR-0062. Orthogonal to visa type - describes how the visa is processed.
// Existing rows default to STANDARD (no appointment) so nothing changes for
// them. APPOINTMENT_EMBASSY triggers the "embassy fees at counter" note in
// the itinerary picker + preview + share page.
const PROCESSING_MODE_OPTIONS = [
  { value: 'STANDARD', label: 'Standard (no appointment)' },
  { value: 'APPOINTMENT_ONLY', label: 'Appointment only' },
  { value: 'APPOINTMENT_EMBASSY', label: 'Appointment + Embassy visit' },
];

interface VisaFormValues {
  status: VisaDetail['status'];
  legacyCode: string | null;
  slug: string | null;
  name: string;
  visaType: VisaDetail['visaType'];
  entryType: VisaDetail['entryType'];
  processingMode: VisaDetail['processingMode'];
  targetCountryId: string | null;
  validityMonths: number;
  maxStayDays: number;
  requiredDocumentsJoined: string;
  eligibleNationalitiesJoined: string;
  excludedNationalitiesJoined: string;
  vendorSupplierId: string | null;
  contractRef: string | null;
  shortDescription: string | null;
  longDescriptionHtml: string | null;
  termsHtml: string | null;
  cancellationHtml: string | null;
  validityFrom: string | null;
  validityTo: string | null;
  photos: GalleryPhoto[];
  metaTagName: string | null;
}

const CREATE_DEFAULTS: VisaFormValues = {
  status: 'DRAFT',
  legacyCode: null,
  slug: null,
  name: '',
  visaType: 'TOURIST',
  entryType: 'SINGLE',
  processingMode: 'STANDARD',
  targetCountryId: null,
  validityMonths: 6,
  maxStayDays: 30,
  requiredDocumentsJoined: '',
  eligibleNationalitiesJoined: '',
  excludedNationalitiesJoined: '',
  vendorSupplierId: null,
  contractRef: null,
  shortDescription: null,
  longDescriptionHtml: null,
  termsHtml: null,
  cancellationHtml: null,
  validityFrom: null,
  validityTo: null,
  photos: [],
  metaTagName: null,
};

const trimOrNull = (v: string | null | undefined): string | null => {
  if (v === null || v === undefined) return null;
  const t = v.trim();
  return t === '' ? null : t;
};

const splitLines = (input: string): string[] =>
  input
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.length > 0);

const splitNationalities = (input: string): string[] =>
  input
    .split(/[,\n\s]+/)
    .map((s) => s.trim().toUpperCase())
    .filter((s) => s.length >= 2 && s.length <= 3);

const parsePhotos = (a: unknown): GalleryPhoto[] => {
  if (!Array.isArray(a)) return [];
  return a.filter(
    (p): p is GalleryPhoto =>
      Boolean(p) &&
      typeof p === 'object' &&
      (typeof (p as { url?: unknown }).url === 'string' ||
        typeof (p as { mediaId?: unknown }).mediaId === 'string'),
  );
};

const toFormValues = (v: VisaDetail | null): VisaFormValues => {
  if (!v) return CREATE_DEFAULTS;
  return {
    status: v.status,
    legacyCode: v.legacyCode,
    slug: v.slug,
    name: v.name,
    visaType: v.visaType,
    entryType: v.entryType,
    processingMode: v.processingMode,
    targetCountryId: v.targetCountryId,
    validityMonths: v.validityMonths,
    maxStayDays: v.maxStayDays,
    requiredDocumentsJoined: v.requiredDocuments.join('\n'),
    eligibleNationalitiesJoined: v.eligibleNationalities.join(', '),
    excludedNationalitiesJoined: v.excludedNationalities.join(', '),
    vendorSupplierId: v.vendorSupplierId,
    contractRef: v.contractRef,
    shortDescription: v.shortDescription,
    longDescriptionHtml: v.longDescriptionHtml,
    termsHtml: v.termsHtml,
    cancellationHtml: v.cancellationHtml,
    validityFrom: v.validityFrom,
    validityTo: v.validityTo,
    photos: parsePhotos(v.photos),
    metaTagName: v.metaTagName,
  };
};

const fromFormValues = (values: VisaFormValues): CreateVisaRequest => ({
  status: values.status,
  legacyCode: trimOrNull(values.legacyCode),
  slug: trimOrNull(values.slug),
  name: values.name.trim(),
  visaType: values.visaType,
  entryType: values.entryType,
  processingMode: values.processingMode,
  targetCountryId: values.targetCountryId ?? null,
  validityMonths: values.validityMonths,
  maxStayDays: values.maxStayDays,
  requiredDocuments: splitLines(values.requiredDocumentsJoined),
  eligibleNationalities: splitNationalities(values.eligibleNationalitiesJoined),
  excludedNationalities: splitNationalities(values.excludedNationalitiesJoined),
  vendorSupplierId: values.vendorSupplierId ?? null,
  contractRef: trimOrNull(values.contractRef),
  shortDescription: trimOrNull(values.shortDescription),
  longDescriptionHtml: trimOrNull(values.longDescriptionHtml),
  termsHtml: trimOrNull(values.termsHtml),
  cancellationHtml: trimOrNull(values.cancellationHtml),
  validityFrom: values.validityFrom || null,
  validityTo: values.validityTo || null,
  photos: values.photos.length > 0 ? values.photos : null,
  metaTagName: trimOrNull(values.metaTagName),
});

/** "YYYY-MM-DD" -> local Date (midnight), for UzDateTimePicker. */
const ymdToDate = (s: string | null | undefined): Date | null => {
  if (!s) return null;
  const [y, m, d] = s.split('-').map(Number);
  if (!y || !m || !d) return null;
  return new Date(y, m - 1, d);
};
/** Date -> "YYYY-MM-DD" (local). */
const dateToYmd = (d: Date | null): string =>
  d
    ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
    : '';

/** Shopify-style card: title header + stacked fields. */
const Card = ({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}): React.JSX.Element => (
  <section className="rounded-lg border border-zinc-200 bg-white">
    <header className="border-b border-zinc-100 px-5 py-3.5">
      <h3 className="text-sm font-semibold tracking-tight text-zinc-900">{title}</h3>
      {description && <p className="mt-0.5 text-xs text-zinc-500">{description}</p>}
    </header>
    <div className="space-y-5 px-5 py-5">{children}</div>
  </section>
);

export const VisaEditPage = (): React.JSX.Element => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const backToList = useListReturn('/visas');
  const me = useCurrentUser();
  const canApprove = me?.role === 'ADMIN';
  const isCreate = !id || id === 'new';
  const [activeTab, setActiveTab] = useState<string>('details');

  const detailQuery = useVisaDetail(isCreate ? null : (id ?? null));
  const visa = detailQuery.data ?? null;

  const createVisa = useCreateVisa();
  const updateVisa = useUpdateVisa();
  const approveVisa = useApproveVisa();
  const pending = createVisa.isPending || updateVisa.isPending || approveVisa.isPending;

  const form = useForm<VisaFormValues>({ defaultValues: toFormValues(null) });

  useEffect(() => {
    if (isCreate) form.reset(toFormValues(null));
    else if (visa) form.reset(toFormValues(visa));
  }, [isCreate, visa, form]);

  const submit = form.handleSubmit(async (values) => {
    if (!values.slug || !values.slug.trim()) {
      form.setError('slug', { message: 'URL slug is required (use the wand to generate)' });
      setActiveTab('details');
      return;
    }
    if (!values.targetCountryId) {
      form.setError('targetCountryId', { message: 'Target country is required' });
      setActiveTab('details');
      return;
    }
    const payload = fromFormValues(values);
    if (isCreate) {
      const created = await createVisa.mutateAsync(payload);
      navigate(`/visas/${created.id}/edit`);
    } else if (visa) {
      await updateVisa.mutateAsync({ id: visa.id, payload });
    }
  });

  const tabs = useMemo(() => {
    const list: { value: string; label: ReactNode; content: ReactNode }[] = [
      {
        value: 'details',
        label: 'Details',
        content: (
          <UzForm form={form} onSubmit={submit}>
            <div className="grid items-start gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
              <div className="min-w-0 space-y-6">
                <IdentityCard form={form} pending={pending} />
                <TargetCard pending={pending} />
                <DocumentationCard pending={pending} />
              </div>
              <aside className="space-y-6 self-start lg:sticky lg:top-8">
                <StatusCard
                  pending={pending}
                  visa={visa}
                  canApprove={canApprove}
                  onToggleApprove={(tier, approve) =>
                    visa && approveVisa.mutate({ id: visa.id, payload: { tier, approve } })
                  }
                />
                <CommercialCard pending={pending} />
              </aside>
            </div>
          </UzForm>
        ),
      },
    ];
    if (!isCreate && visa) {
      list.push({
        value: 'pricing',
        label: `Pricing tiers (${visa.pricingTierCount})`,
        content: <PricingTiersTab visaId={visa.id} />,
      });
    }
    list.push({
      value: 'content',
      label: 'Content & media',
      content: (
        <UzForm form={form} onSubmit={submit}>
          <div className="mx-auto max-w-3xl space-y-6">
            <ContentCard pending={pending} />
            <MediaCard pending={pending} />
          </div>
        </UzForm>
      ),
    });
    return list;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- submit stable per render
  }, [isCreate, visa, canApprove, form, pending, approveVisa]);

  const statusDot =
    visa?.status === 'ACTIVE'
      ? 'bg-emerald-500'
      : visa?.status === 'DRAFT' || visa?.status === 'PENDING_APPROVAL'
        ? 'bg-amber-500'
        : 'bg-zinc-400';
  const statusText =
    visa?.status === 'ACTIVE'
      ? 'Active'
      : visa?.status === 'DRAFT'
        ? 'Draft'
        : visa?.status === 'PENDING_APPROVAL'
          ? 'Pending approval'
          : visa?.status === 'INACTIVE'
            ? 'Inactive'
            : visa?.status === 'ARCHIVED'
              ? 'Archived'
              : '';

  return (
    <div className="-mx-4 -mt-6 flex min-h-full flex-col lg:-mx-8 lg:-mt-8">
      {/* Sticky top bar - breaks out of the scroll container's padding so it sits
          flush under the app header and stays solid (content never shows through). */}
      <div className="sticky -top-6 z-30 flex h-12 items-center gap-3 border-b border-zinc-200 bg-white px-6 lg:-top-8 lg:px-8">
        <button
          type="button"
          onClick={backToList}
          className="-ml-1 inline-flex h-7 w-7 items-center justify-center rounded text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900"
          aria-label="Back to visas"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="flex min-w-0 items-center gap-2">
          <span className="text-xs font-medium text-zinc-500">Visas</span>
          <span className="text-zinc-300" aria-hidden>
            /
          </span>
          <span className="truncate text-sm font-semibold tracking-tight text-zinc-900">
            {isCreate ? 'Add visa' : visa ? visa.name : 'Edit'}
          </span>
        </div>
        <span className="flex-1" />
        {!isCreate && visa && (
          <span className="inline-flex items-center gap-1.5 text-xs text-zinc-600">
            <span className={'h-1.5 w-1.5 rounded-full ' + statusDot} aria-hidden />
            {statusText}
          </span>
        )}
        <UzButton variant="ghost" size="sm" onClick={backToList} disabled={pending}>
          Cancel
        </UzButton>
        <UzButton size="sm" onClick={submit} loading={pending}>
          <span>{isCreate ? 'Create' : 'Save'}</span>
          <kbd className="ml-2 hidden h-4 items-center rounded border border-zinc-300/60 bg-white/15 px-1 font-mono text-[10px] text-current/80 sm:inline-flex">
            ⌘S
          </kbd>
        </UzButton>
      </div>

      <div className="mx-auto w-full max-w-6xl min-w-0 px-6 py-6 lg:px-8">
        {!isCreate && visa && (
          <div className="mb-6 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-zinc-500">
            {visa.isApprovedByAdmin && (
              <span className="inline-flex items-center gap-1 text-emerald-700">
                <span className="h-1 w-1 rounded-full bg-emerald-500" aria-hidden />
                Admin approved
              </span>
            )}
            {visa.isApprovedByModerator && <span>Moderator approved</span>}
            {visa.legacyId !== null && (
              <span>
                Legacy{' '}
                <span className="font-mono text-zinc-700 tabular-nums">#{visa.legacyId}</span>
              </span>
            )}
            {visa.legacyCode && (
              <span>
                Code <span className="font-mono text-zinc-700">{visa.legacyCode}</span>
              </span>
            )}
          </div>
        )}

        {!isCreate && detailQuery.isLoading && <CatalogEditorSkeleton />}
        {!isCreate && detailQuery.isError && (
          <UzAlert tone="destructive">Failed to load visa detail. Go back and retry.</UzAlert>
        )}

        {(isCreate || visa) && (
          <UzTabs value={activeTab} onValueChange={setActiveTab} tabs={tabs} />
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Cards
// ---------------------------------------------------------------------------

interface CardProps {
  pending: boolean;
}
interface FormCardProps extends CardProps {
  form: UseFormReturn<VisaFormValues>;
}

const IdentityCard = ({ form, pending }: FormCardProps): React.JSX.Element => (
  <Card title="Identity">
    <UzFormField name="name">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | undefined) ?? ''}
          label="Visa name"
          placeholder="e.g. UAE Tourist Visa - 30 day single entry"
          error={error}
          required
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="slug">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="URL slug"
          required
          error={error}
          disabled={pending}
          rightAdornment={
            <UzTooltip label="Auto-generate slug">
              <button
                type="button"
                disabled={pending}
                aria-label="Auto-generate slug"
                onClick={() => {
                  const name = (form.getValues('name') ?? '').trim();
                  if (name) field.onChange(slugify(name));
                }}
                className="text-zinc-400 transition-colors hover:text-zinc-900 disabled:cursor-not-allowed disabled:opacity-50"
              >
                <Sparkles className="h-4 w-4" />
              </button>
            </UzTooltip>
          }
        />
      )}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="visaType">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Visa type"
            options={VISA_TYPE_OPTIONS}
            value={(field.value as string | undefined) || 'TOURIST'}
            onValueChange={(v) => field.onChange(v)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="entryType">
        {({ field, error }) => (
          <UzSelect
            name={field.name}
            label="Entry type"
            options={ENTRY_TYPE_OPTIONS}
            value={(field.value as string | undefined) || 'SINGLE'}
            onValueChange={(v) => field.onChange(v)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
    <UzFormField name="processingMode">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Processing mode"
          hint="How this visa is processed. Appointment-only asks the vendor to book a slot; Appointment + Embassy adds an in-person embassy visit (fees payable at counter)."
          options={PROCESSING_MODE_OPTIONS}
          value={(field.value as string | undefined) || 'STANDARD'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

const TargetCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Target & validity">
    <UzFormField name="targetCountryId">
      {({ field, error }) => (
        <CountryPicker
          label="Target country"
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          required
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="validityMonths">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Validity (months)"
            precision={0}
            min={0}
            max={120}
            value={field.value === null || field.value === undefined ? 6 : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 6)}
            hint="Usable from issue date"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="maxStayDays">
        {({ field, error }) => (
          <UzNumberInput
            name={field.name}
            label="Max stay (days)"
            precision={0}
            min={0}
            max={365}
            value={field.value === null || field.value === undefined ? 30 : (field.value as number)}
            onValueChange={(v) => field.onChange(v ?? 30)}
            hint="Per-entry stay limit"
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="validityFrom">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            label="Quote valid from"
            value={ymdToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="validityTo">
        {({ field, error }) => (
          <UzDateTimePicker
            mode="date"
            displayFormat="dd/MM/yyyy"
            placeholder="dd/mm/yyyy"
            label="Quote valid to"
            value={ymdToDate(field.value as string | null | undefined)}
            onValueChange={(d) => field.onChange(dateToYmd(d) || null)}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

const DocumentationCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Documentation & eligibility">
    <UzFormField name="requiredDocumentsJoined">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value)}
          label="Required documents"
          placeholder={
            'Passport (6 month validity)\nPhotograph - 35x45mm white background\nHotel booking confirmation'
          }
          hint="One document per line."
          rows={6}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <div className="grid gap-5 sm:grid-cols-2">
      <UzFormField name="eligibleNationalitiesJoined">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            label="Eligible nationalities"
            placeholder="IN, AE, GB"
            hint="ISO 2/3 codes, comma or newline separated. Empty = any."
            rows={3}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="excludedNationalitiesJoined">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value)}
            label="Excluded nationalities"
            placeholder="None"
            hint="Pickers hide this visa for these nationalities."
            rows={3}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

// ----- Sidebar cards -----

interface StatusCardProps extends CardProps {
  visa: VisaDetail | null;
  canApprove: boolean;
  onToggleApprove: (tier: 'admin' | 'moderator', approve: boolean) => void;
}

const StatusCard = ({
  pending,
  visa,
  canApprove,
  onToggleApprove,
}: StatusCardProps): React.JSX.Element => (
  <Card title="Status">
    <UzFormField name="status">
      {({ field, error }) => (
        <UzSelect
          name={field.name}
          label="Lifecycle"
          options={
            canApprove
              ? STATUS_OPTIONS
              : STATUS_OPTIONS.filter((o) => o.value === 'DRAFT' || o.value === 'PENDING_APPROVAL')
          }
          value={(field.value as string | undefined) || 'DRAFT'}
          onValueChange={(v) => field.onChange(v)}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    {visa && canApprove && (
      <>
        <UzSwitch
          label={visa.isApprovedByAdmin ? 'Admin approved' : 'Admin not approved'}
          description="Admin approval is the gate to status=ACTIVE."
          checked={visa.isApprovedByAdmin}
          onCheckedChange={(checked) => onToggleApprove('admin', Boolean(checked))}
          disabled={pending}
        />
        <UzSwitch
          label={visa.isApprovedByModerator ? 'Moderator approved' : 'Moderator not approved'}
          description="Moderator sign-off is informational."
          checked={visa.isApprovedByModerator}
          onCheckedChange={(checked) => onToggleApprove('moderator', Boolean(checked))}
          disabled={pending}
        />
      </>
    )}
  </Card>
);

const CommercialCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Commercial">
    <UzFormField name="vendorSupplierId">
      {({ field, error }) => (
        <SupplierPicker
          value={(field.value as string | null | undefined) ?? null}
          onValueChange={(v) => field.onChange(v)}
          disabled={pending}
          error={error}
        />
      )}
    </UzFormField>
    <UzFormField name="contractRef">
      {({ field, error }) => (
        <UzInput
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Contract reference"
          placeholder="Internal contract / PO number"
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

// ----- Content & media -----

const ContentCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Descriptions & policy">
    <UzFormField name="shortDescription">
      {({ field, error }) => (
        <UzTextarea
          {...field}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
          label="Short description"
          placeholder="One-sentence summary for picker cards."
          rows={2}
          error={error}
          disabled={pending}
        />
      )}
    </UzFormField>
    <UzFormField name="longDescriptionHtml">
      {({ field }) => (
        <RichTextEditor
          label="Long description"
          placeholder="Multi-paragraph description with formatting..."
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(html) => field.onChange(html === '' ? null : html)}
          disabled={pending}
        />
      )}
    </UzFormField>
    <div className="grid gap-5 md:grid-cols-2">
      <UzFormField name="termsHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Terms & conditions"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
      <UzFormField name="cancellationHtml">
        {({ field, error }) => (
          <UzTextarea
            {...field}
            value={(field.value as string | null | undefined) ?? ''}
            onChange={(e) => field.onChange(e.target.value === '' ? null : e.target.value)}
            label="Cancellation policy"
            rows={6}
            error={error}
            disabled={pending}
          />
        )}
      </UzFormField>
    </div>
  </Card>
);

const MediaCard = ({ pending }: CardProps): React.JSX.Element => (
  <Card title="Photos">
    <UzFormField name="photos">
      {({ field }) => (
        <PhotoGalleryField
          photos={(field.value as GalleryPhoto[]) ?? []}
          onChange={(next) => field.onChange(next)}
          folder="visas"
          disabled={pending}
        />
      )}
    </UzFormField>
  </Card>
);

// ---------------------------------------------------------------------------
// Pricing tiers tab (sub-resource CRUD)
// ---------------------------------------------------------------------------

interface PricingTiersTabProps {
  visaId: string;
}

const PricingTiersTab = ({ visaId }: PricingTiersTabProps): React.JSX.Element => {
  const tiersQuery = useVisaPricingTiers(visaId);
  const createTier = useCreateVisaPricingTier();
  const updateTier = useUpdateVisaPricingTier();
  const deleteTier = useDeleteVisaPricingTier();

  const [showForm, setShowForm] = useState(false);
  const [editingTier, setEditingTier] = useState<VisaPricingTierOut | null>(null);

  const tiers = tiersQuery.data ?? [];

  const handleAdd = () => {
    setEditingTier(null);
    setShowForm(true);
  };

  const handleEdit = (tier: VisaPricingTierOut) => {
    setEditingTier(tier);
    setShowForm(true);
  };

  const handleDelete = async (tier: VisaPricingTierOut) => {
    if (!window.confirm(`Delete tier "${tier.tierName}"? This soft-deletes it.`)) return;
    await deleteTier.mutateAsync({ visaId, tierId: tier.id });
  };

  return (
    <div className="space-y-5">
      <UzAlert tone="info">
        One tier per urgency band (Standard / Express / Same-day). Each row carries its own
        currency, fee, and processing time. The picker shows the cheapest tier first.
      </UzAlert>

      {tiersQuery.isLoading && <p className="text-sm text-zinc-500">Loading pricing tiers...</p>}
      {tiersQuery.isError && <UzAlert tone="destructive">Failed to load pricing tiers.</UzAlert>}

      <div className="space-y-3">
        {tiers.map((tier) => (
          <UzCard key={tier.id} className="flex items-start justify-between p-4">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <span className="font-medium text-zinc-900">{tier.tierName}</span>
                <UzBadge tone="secondary">
                  {tier.processingDays} day{tier.processingDays === 1 ? '' : 's'}
                </UzBadge>
                {!tier.isActive && <UzBadge tone="warning">Inactive</UzBadge>}
                {tier.legacyId !== null && (
                  <UzBadge tone="secondary">legacy #{tier.legacyId}</UzBadge>
                )}
              </div>
              <div className="mt-2 grid grid-cols-1 gap-3 text-sm text-zinc-600 sm:grid-cols-2">
                <div>
                  Applicant fee:{' '}
                  <span className="font-mono text-zinc-900">
                    {tier.currencyCode ?? 'INR'} {tier.feePerApplicant.toFixed(2)}
                  </span>
                </div>
                <div>
                  Agency charge:{' '}
                  <span className="font-mono text-zinc-900">
                    {tier.currencyCode ?? 'INR'} {tier.agencyCharge.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <UzButton variant="ghost" size="sm" onClick={() => handleEdit(tier)}>
                Edit
              </UzButton>
              <UzButton
                variant="ghost"
                size="sm"
                onClick={() => handleDelete(tier)}
                disabled={deleteTier.isPending}
              >
                <Trash2 className="h-4 w-4 text-red-600" />
              </UzButton>
            </div>
          </UzCard>
        ))}
        {tiers.length === 0 && !tiersQuery.isLoading && (
          <p className="text-sm text-zinc-500">No pricing tiers yet. Add a Standard tier first.</p>
        )}
      </div>

      {!showForm && (
        <UzButton size="sm" onClick={handleAdd}>
          <Plus className="mr-1 h-4 w-4" />
          Add pricing tier
        </UzButton>
      )}

      {showForm && (
        <PricingTierForm
          initial={editingTier}
          pending={createTier.isPending || updateTier.isPending}
          onSubmit={async (payload) => {
            if (editingTier) {
              await updateTier.mutateAsync({ visaId, tierId: editingTier.id, payload });
            } else {
              await createTier.mutateAsync({ visaId, payload });
            }
            setShowForm(false);
            setEditingTier(null);
          }}
          onCancel={() => {
            setShowForm(false);
            setEditingTier(null);
          }}
        />
      )}
    </div>
  );
};

interface PricingTierFormProps {
  initial: VisaPricingTierOut | null;
  pending: boolean;
  onSubmit: (payload: CreateVisaPricingTierRequest) => Promise<void>;
  onCancel: () => void;
}

interface TierFormValues {
  tierName: string;
  processingDays: number;
  currencyId: string | null;
  feePerApplicant: number;
  agencyCharge: number;
  isActive: boolean;
  sortOrder: number;
}

const PricingTierForm = ({
  initial,
  pending,
  onSubmit,
  onCancel,
}: PricingTierFormProps): React.JSX.Element => {
  const form = useForm<TierFormValues>({
    defaultValues: initial
      ? {
          tierName: initial.tierName,
          processingDays: initial.processingDays,
          currencyId: initial.currencyId,
          feePerApplicant: initial.feePerApplicant,
          agencyCharge: initial.agencyCharge,
          isActive: initial.isActive,
          sortOrder: initial.sortOrder,
        }
      : {
          tierName: 'Standard',
          processingDays: 7,
          currencyId: null,
          feePerApplicant: 0,
          agencyCharge: 0,
          isActive: true,
          sortOrder: 0,
        },
  });

  // Default a new tier's currency to INR once the list loads.
  const currenciesQuery = useCurrenciesList({ page: 1, pageSize: 200, sortDir: 'asc' });
  useEffect(() => {
    if (initial) return;
    if (form.getValues('currencyId')) return;
    const inr = currenciesQuery.data?.data.find((c) => c.code === 'INR');
    if (inr) form.setValue('currencyId', inr.id);
  }, [initial, currenciesQuery.data, form]);

  const submit = form.handleSubmit(async (values) => {
    await onSubmit({
      tierName: values.tierName.trim(),
      processingDays: values.processingDays,
      currencyId: values.currencyId ?? null,
      feePerApplicant: values.feePerApplicant || 0,
      agencyCharge: values.agencyCharge || 0,
      isActive: values.isActive,
      sortOrder: values.sortOrder,
    });
  });

  return (
    <UzCard className="space-y-4 border border-zinc-200 bg-zinc-50 p-5">
      <h4 className="font-medium text-zinc-900">
        {initial ? `Edit ${initial.tierName}` : 'New pricing tier'}
      </h4>
      <UzForm form={form} onSubmit={submit}>
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <UzFormField name="tierName">
              {({ field, error }) => (
                <UzInput
                  {...field}
                  value={(field.value as string | undefined) ?? ''}
                  label="Tier name"
                  placeholder="Standard / Express / Same-day"
                  error={error}
                  required
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="processingDays">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Processing days"
                  precision={0}
                  min={0}
                  max={365}
                  value={
                    field.value === null || field.value === undefined ? 0 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 0)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
          <UzFormField name="currencyId">
            {({ field, error }) => (
              <CurrencyPicker
                value={(field.value as string | null | undefined) ?? null}
                onValueChange={(v) => field.onChange(v)}
                disabled={pending}
                error={error}
              />
            )}
          </UzFormField>
          <div className="grid gap-4 md:grid-cols-2">
            <UzFormField name="feePerApplicant">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  precision={2}
                  min={0}
                  label="Fee per applicant"
                  value={(field.value as number | undefined) || ''}
                  onValueChange={(v) => field.onChange(v ?? 0)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="agencyCharge">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  precision={2}
                  min={0}
                  label="Agency charge"
                  hint="Per-application service fee Utazzo retains."
                  value={(field.value as number | undefined) || ''}
                  onValueChange={(v) => field.onChange(v ?? 0)}
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <UzFormField name="sortOrder">
              {({ field, error }) => (
                <UzNumberInput
                  name={field.name}
                  label="Sort order"
                  precision={0}
                  min={0}
                  value={
                    field.value === null || field.value === undefined ? 0 : (field.value as number)
                  }
                  onValueChange={(v) => field.onChange(v ?? 0)}
                  hint="Lower first in picker cards"
                  error={error}
                  disabled={pending}
                />
              )}
            </UzFormField>
            <UzFormField name="isActive">
              {({ field }) => (
                <UzSwitch
                  label="Active"
                  description="Inactive tiers are hidden from pickers."
                  checked={field.value !== false}
                  onCheckedChange={field.onChange}
                  disabled={pending}
                />
              )}
            </UzFormField>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <UzButton variant="ghost" size="sm" onClick={onCancel} disabled={pending}>
              Cancel
            </UzButton>
            <UzButton size="sm" onClick={submit} loading={pending}>
              {initial ? 'Save tier' : 'Add tier'}
            </UzButton>
          </div>
        </div>
      </UzForm>
    </UzCard>
  );
};
